import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Check } from "lucide-react";
import { toast } from "sonner";
import { api, BODY_PARTS, fmtErr } from "@/lib/api";
import { wa, WA_MESSAGES } from "@/components/WhatsApp";

const STEPS = [
  { key: "experience", title: "How was today's workout?", options: ["Poor", "Okay", "Good", "Great", "Excellent"] },
  { key: "difficulty", title: "How challenging was today's session?", options: ["Very Easy", "Easy", "Moderate", "Hard", "Very Hard"] },
  { key: "energy", title: "How was your energy during the workout?", options: ["Low", "Below Average", "Good", "High", "Excellent"] },
  { key: "comfort", title: "Did the exercises feel comfortable and natural?",
    options: ["Yes, completely", "Mostly", "Some exercises felt uncomfortable", "No"] },
  { key: "pain", title: "Did you experience unusual pain or discomfort?", options: ["No", "Yes"] },
  { key: "rpe", title: "How hard did the session feel overall?", type: "rpe" },
  { key: "enjoyment", title: "Did you enjoy today's workout?", type: "enjoy" },
  { key: "confidence", title: "How confident did you feel performing today's exercises?",
    options: ["Not Confident", "Slightly Confident", "Comfortable", "Confident", "Very Confident"] },
];

export default function WorkoutFlow({ plan, date, onClose, onDone }) {
  const [stage, setStage] = useState("log"); // log | 0..7 | note | done
  const [results, setResults] = useState(() =>
    plan.blocks.flatMap((b) => b.items).map((i) => ({ name: i.name, done: false, weight: "", reps: "" })));
  const [fb, setFb] = useState({ rpe: 6, enjoyment: 4, pain_area: "", pain_exercise: "", pain_desc: "",
    pain_severity: "Mild", uncomfortable_exercise: "", note: "" });
  const [busy, setBusy] = useState(false);

  const doneCount = results.filter((r) => r.done).length;
  const completion = results.length ? Math.round((doneCount / results.length) * 100) : 100;

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/me/logs", { plan_id: plan.id, date, results, completion_pct: completion, notes: fb.note });
      await api.post("/me/feedback", {
        plan_id: plan.id, date,
        experience: fb.experience, difficulty: fb.difficulty, energy: fb.energy, comfort: fb.comfort,
        uncomfortable_exercise: fb.uncomfortable_exercise,
        pain: fb.pain === "Yes", pain_area: fb.pain_area, pain_exercise: fb.pain_exercise,
        pain_desc: fb.pain_desc, pain_severity: fb.pain_severity,
        rpe: fb.rpe, enjoyment: fb.enjoyment, confidence: fb.confidence, note: fb.note,
      });
      setStage("done");
      onDone?.();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const isFeedbackStep = typeof stage === "number";
  const step = isFeedbackStep ? STEPS[stage] : null;
  const progressLabel = stage === "log" ? "Log exercises" : isFeedbackStep ? `${stage + 1} of ${STEPS.length}` : stage === "note" ? "Almost done" : "";

  return (
    <div className="fixed inset-0 z-[85] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4" data-testid="workout-flow">
      <div className="w-full max-w-lg bg-zinc-950 border border-zinc-800 p-6 sm:p-8 max-h-[88vh] overflow-y-auto" style={{ overscrollBehavior: "contain" }}>
        <div className="flex items-center justify-between mb-5">
          <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-bold">{progressLabel}</p>
          <button onClick={onClose} data-testid="flow-close" className="text-zinc-500 hover:text-volt text-xs uppercase tracking-[0.2em]">Exit</button>
        </div>

        <AnimatePresence mode="wait">
          {stage === "log" && (
            <motion.div key="log" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <h3 className="font-display uppercase text-3xl leading-none">Mark what you <span className="text-volt">completed.</span></h3>
              <div className="mt-5 space-y-2 max-h-[46vh] overflow-y-auto pr-1" style={{ overscrollBehavior: "contain" }}>
                {results.map((r, i) => (
                  <div key={i} data-testid={`log-exercise-${i}`} className={`border p-3 transition-colors ${r.done ? "border-volt/60 bg-volt/5" : "border-zinc-800"}`}>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input type="checkbox" checked={r.done} data-testid={`log-check-${i}`}
                        onChange={(e) => setResults(results.map((x, j) => j === i ? { ...x, done: e.target.checked } : x))}
                        className="accent-[#ff5a1f] w-4 h-4" />
                      <span className="text-sm font-semibold text-zinc-100 flex-1">{r.name}</span>
                    </label>
                    {r.done && (
                      <div className="flex gap-2 mt-2 pl-7">
                        <input placeholder="Weight (kg)" value={r.weight} data-testid={`log-weight-${i}`}
                          onChange={(e) => setResults(results.map((x, j) => j === i ? { ...x, weight: e.target.value } : x))}
                          className="w-28 bg-ink border border-zinc-700 px-2.5 py-1.5 text-xs focus:border-volt outline-none" />
                        <input placeholder="Reps done" value={r.reps} data-testid={`log-reps-${i}`}
                          onChange={(e) => setResults(results.map((x, j) => j === i ? { ...x, reps: e.target.value } : x))}
                          className="w-28 bg-ink border border-zinc-700 px-2.5 py-1.5 text-xs focus:border-volt outline-none" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <button data-testid="flow-to-feedback" onClick={() => setStage(0)}
                className="mt-5 w-full bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold py-4 hover:bg-white transition-colors">
                Complete session · {completion}%
              </button>
            </motion.div>
          )}

          {isFeedbackStep && (
            <motion.div key={stage} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}>
              <h3 className="font-display uppercase text-3xl leading-[0.95]" data-testid="flow-question">{step.title}</h3>
              {step.type === "rpe" ? (
                <div className="mt-6">
                  <p className="font-display text-7xl text-volt text-center">{fb.rpe}</p>
                  <input type="range" min="1" max="10" value={fb.rpe} data-testid="flow-rpe"
                    onChange={(e) => setFb({ ...fb, rpe: Number(e.target.value) })} className="w-full accent-[#ff5a1f] mt-4" />
                  <div className="flex justify-between text-[10px] uppercase tracking-[0.14em] text-zinc-500 mt-1">
                    <span>1–2 Very easy</span><span>5–6 Moderate</span><span>10 Max</span>
                  </div>
                </div>
              ) : step.type === "enjoy" ? (
                <div className="mt-6 flex justify-between gap-2">
                  {[1, 2, 3, 4, 5].map((v) => (
                    <button key={v} data-testid={`flow-enjoy-${v}`} onClick={() => setFb({ ...fb, enjoyment: v })}
                      className={`flex-1 py-4 font-display text-2xl border transition-colors ${fb.enjoyment === v ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-400 hover:border-volt"}`}>
                      {v}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="mt-6 flex flex-col gap-2">
                  {step.options.map((o) => (
                    <button key={o} data-testid={`flow-opt-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      onClick={() => setFb({ ...fb, [step.key]: o })}
                      className={`border px-5 py-3.5 text-left text-sm font-bold transition-colors ${fb[step.key] === o ? "bg-volt text-black border-volt" : "border-zinc-700 text-zinc-300 hover:border-volt"}`}>
                      {o}
                    </button>
                  ))}
                </div>
              )}

              {step.key === "comfort" && (fb.comfort === "Some exercises felt uncomfortable" || fb.comfort === "No") && (
                <input placeholder="Which exercise felt uncomfortable?" value={fb.uncomfortable_exercise}
                  data-testid="flow-uncomfortable"
                  onChange={(e) => setFb({ ...fb, uncomfortable_exercise: e.target.value })}
                  className="mt-3 w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
              )}
              {step.key === "pain" && fb.pain === "Yes" && (
                <div className="mt-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <select value={fb.pain_area} data-testid="flow-pain-area"
                      onChange={(e) => setFb({ ...fb, pain_area: e.target.value })}
                      className="bg-ink border border-zinc-700 px-3 py-2.5 text-xs text-zinc-300 focus:border-volt outline-none">
                      <option value="">Body area</option>
                      {BODY_PARTS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select>
                    <select value={fb.pain_severity} data-testid="flow-pain-severity"
                      onChange={(e) => setFb({ ...fb, pain_severity: e.target.value })}
                      className="bg-ink border border-zinc-700 px-3 py-2.5 text-xs text-zinc-300 focus:border-volt outline-none">
                      {["Mild", "Moderate", "Severe"].map((s) => <option key={s}>{s}</option>)}
                    </select>
                  </div>
                  <input placeholder="Exercise (optional)" value={fb.pain_exercise} data-testid="flow-pain-exercise"
                    onChange={(e) => setFb({ ...fb, pain_exercise: e.target.value })}
                    className="w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
                  <input placeholder="Short description" value={fb.pain_desc} data-testid="flow-pain-desc"
                    onChange={(e) => setFb({ ...fb, pain_desc: e.target.value })}
                    className="w-full bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
                  <p className="text-[10px] text-zinc-600">This is training feedback, not a medical diagnosis.</p>
                </div>
              )}

              <div className="mt-6 flex gap-2">
                <button data-testid="flow-back" onClick={() => setStage(stage === 0 ? "log" : stage - 1)}
                  className="border border-zinc-700 px-5 py-3.5 text-xs uppercase tracking-[0.14em] font-bold hover:border-volt hover:text-volt transition-colors">
                  <ArrowLeft size={14} />
                </button>
                <button data-testid="flow-next" disabled={!step.type && !fb[step.key]}
                  onClick={() => setStage(stage === STEPS.length - 1 ? "note" : stage + 1)}
                  className="flex-1 bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold py-3.5 hover:bg-white transition-colors disabled:opacity-40">
                  {stage === STEPS.length - 1 ? "Last step" : "Next"}
                </button>
              </div>
            </motion.div>
          )}

          {stage === "note" && (
            <motion.div key="note" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }}>
              <h3 className="font-display uppercase text-3xl leading-none">Anything you'd like <span className="text-volt">your coach</span> to know?</h3>
              <textarea rows={4} value={fb.note} data-testid="flow-note"
                onChange={(e) => setFb({ ...fb, note: e.target.value })}
                placeholder="Tell your trainer what felt great, difficult, uncomfortable, or anything you'd like changed next time..."
                className="mt-5 w-full bg-ink border border-zinc-700 px-4 py-3 text-sm placeholder-zinc-600 focus:border-volt outline-none" />
              <button data-testid="flow-submit" onClick={submit} disabled={busy}
                className="mt-4 w-full bg-volt text-black uppercase tracking-[0.16em] text-xs font-extrabold py-4 hover:bg-white transition-colors disabled:opacity-50">
                {busy ? "Saving…" : "Submit feedback"}
              </button>
            </motion.div>
          )}

          {stage === "done" && (
            <motion.div key="done" initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="text-center py-4">
              <Check size={44} className="text-volt mx-auto" />
              <h3 className="font-display uppercase text-4xl mt-4" data-testid="flow-done">Session logged.</h3>
              <p className="text-sm text-zinc-400 mt-2">Your coach sees this feedback and can reply — check your dashboard.</p>
              <a href={wa(WA_MESSAGES.postWorkout)} target="_blank" rel="noopener noreferrer" data-testid="flow-whatsapp"
                className="mt-6 inline-block border border-[#25D366] text-[#25D366] uppercase tracking-[0.16em] text-xs font-extrabold px-6 py-3.5 hover:bg-[#25D366] hover:text-black transition-colors">
                Message coach on WhatsApp
              </a>
              <button onClick={onClose} data-testid="flow-done-close"
                className="block mx-auto mt-5 text-xs uppercase tracking-[0.2em] text-zinc-500 hover:text-volt transition-colors">
                Back to dashboard
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
